//@ts-ignore
import { Children, cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import _ from "lodash";
import moment from "moment-timezone";
import styled, { css } from "styled-components";

import useFormControl from "../../hooks/useFormControl";
import { getStorage, omitStorage, putStorage, useStorageListener } from "../../hooks/useStorage";
import { getElementParrentsPath } from "../../services/common-service";
import Dropzone, { TDropzoneProps } from "./dropzone";
import Input from "./input";
import Select from "./select";
import { Checkbox, Frame, Radio, Switch, TExtra } from "./styled-templates";
import Textarea from "./textarea";
import Tooltip from "./tooltip";

import ButtonGroup from "components/tools/button-group";
import HoverDatePicker from "components/tools/hover-date-picker";
import DateRangePicker, { DateRangePickerProps } from "components/tools/date-range-picker";
import GalleryInput from "components/tools/gallery-input";
import MapInput from "components/tools/map-input";
import StarsInput from "components/tools/stars-input";
import { TagsInput, TagsSelect } from "components/tools/tags";

import { EForms } from "types/enums";

const useFormName = () => {
    const ref = useRef();
    const [attributes, setAttributes] = useState();
    useEffect(() => {
        setAttributes(getElementParrentsPath(ref.current)?.find?.((i) => i?.nodeName === `FORM`)?.attributes ?? {});
    }, [ref]);
    return {
        controlRef: ref,
        formName: (attributes as any)?.name?.value,
        readOnly: (attributes as any)?.readonly?.value === ``,
        attributes,
    };
};

interface ControlProps {
    name: string;
    label?: string;
    isRequired?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    placeholder?: string;
}

export const Control = {
    Form: styled.form<{ name: EForms; onSubmit: Function; extra?: TExtra }>`
        width: inherit;

        ${({ extra }) => extra}
    `,
    Row: styled(Frame)`
        width: inherit;
        flex-direction: row;
        justify-content: flex-start;
        gap: 0 16px;

        @media (max-width: 768px) {
            gap: 0px 20px;
        }
        @media (max-width: 505px) {
            flex-direction: column;
        }

        ${({ extra }) => extra}
    `,
    Wrapper: (props: any) => {
        const { children, name, label, isRequired: required = false, extra = `` } = props;

        const { controlRef, formName } = useFormName();
        const { readOnly } = useFormControl({ name: formName });
        const value = useStorageListener((state) => _.get(state, `forms.${formName}.values.${name}`));
        const error = useStorageListener((state) => _.get(state, `forms.${formName}.errors.${name}`));
        const warning = useStorageListener((state) => _.get(state, `forms.${formName}.warnings.${name}`));
        const onChange = (e: any) => {
            if (!!formName && !!name) {
                putStorage(`forms.${formName}.values.${name}`, e.target.value);
            }
        };
        return (
            <Frame ref={controlRef} extra={`width: 100%; align-items: flex-start;` + extra}>
                <Control.Label required={required}>{label}</Control.Label>
                {Children.map(children, (child) => {
                    return isValidElement(child)
                        ? cloneElement(child, {
                              // TODO: Redo it with correct TypeScript using
                              // @ts-ignore
                              value: (child as any)?.props?.value ?? getStorage(`forms.${formName}.values.${name}`, ``),
                              onChange: (e: any) => {
                                  if (e?.target?.value?.length >= (child as any)?.props?.maxLength - 5) {
                                      putStorage(
                                          `forms.${formName}.warnings.${name}.message`,
                                          `Не более ${(child as any)?.props?.maxLength} символов (${e.target.value?.length})`,
                                      );
                                  } else {
                                      omitStorage(`forms.${formName}.warnings.${name}`);
                                  }
                                  const newE = (child as any)?.props?.onChange?.(e);
                                  onChange(newE ?? e);
                              },
                              readOnly: readOnly || (child as any)?.props?.readOnly,
                              extra: `width: 100%; flex: 1;` + ((child as any)?.props.extra ?? ``),
                          })
                        : child;
                })}
                <Frame extra={`flex-direction: row; align-items: flex-start; margin-top: 5px;`}>
                    <Control.Error extra={`margin: 0;`}>{error?.message}</Control.Error>
                    <Control.Warning extra={`margin: 0 0 0 5px;`}>{warning?.message}</Control.Warning>
                </Frame>
            </Frame>
        );
    },
    Input: (props: any) => {
        console.log('Onput: render: props = ', props);
        return (
            <Control.Wrapper {...props}>
                <Input {...props} />
            </Control.Wrapper>
        );
    },
    Textarea: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <Textarea {...props} />
            </Control.Wrapper>
        );
    },
    Password: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <Input type={`password`} {...props} />
            </Control.Wrapper>
        );
    },
    Cron: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <Input {...props} />
            </Control.Wrapper>
        );
    },
    Select: (props: { name: string; options: { value: any; label: string; [key: string]: any }[]; [key: string]: any }) => {
        return (
            <Control.Wrapper {...props}>
                <Select {...props} />
            </Control.Wrapper>
        );
    },
    Checkbox: (props: any) => {
        const { checked, label = `Check me` } = props;
        console.log('Checkbox: render: props = ', props);
        return (
            <Control.Wrapper {...props} label="">
                <Frame extra={`flex-direction: row; justify-content: flex-start; > * { width: auto; };`}>
                    <Checkbox checked={checked} onChange={() => {}} {...props} />
                    <Frame extra={`margin-left: 5px;`}>{label}</Frame>
                </Frame>
            </Control.Wrapper>
        );
    },
    Radio: (props: any) => {
        const { checked, label = `Check me` } = props;
        return (
            <Control.Wrapper {...props} label="">
                <Frame extra={`flex-direction: row; justify-content: flex-start; > * { width: auto; };`}>
                    <Radio checked={checked} onChange={() => {}} {...props} />
                    <Frame extra={`margin-left: 5px;`}>{label}</Frame>
                </Frame>
            </Control.Wrapper>
        );
    },
    File: (props: Partial<TDropzoneProps> & { multiple?: boolean; maxFilesNumber?: number }) => {
        return (
            <Control.Wrapper {..._.omit(props, [`children`])}>
                {props.multiple ? <GalleryInput {...(props as any)} /> : <Dropzone {...props} />}
            </Control.Wrapper>
        );
    },
    ButtonGroup: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <ButtonGroup {...props} />
            </Control.Wrapper>
        );
    },
    Map: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <MapInput {...props} />
            </Control.Wrapper>
        );
    },
    Switch: (props: any) => {
        const Label = (labelProps: any) => {
            return (
                <Control.Label
                    {...labelProps}
                    onClick={() => labelProps.onChange({ target: { value: !labelProps.value } })}
                    required={!!props.isRequired}
                    extra={`margin: 0 0 0 15px; justify-content: flex-start; cursor: default;`}
                />
            );
        };
        return (
            <Control.Wrapper
                {..._.omit(props, [`label`])}
                extra={`align-items: center; flex-wrap: wrap; flex-direction: row; ${props.extra}`}
                isRequired={false}
            >
                <Switch {...props} extra="" />
                <Label name={props.name}>{props.label}</Label>
            </Control.Wrapper>
        );
    },
    DateRange: (props: (Omit<DateRangePickerProps, "onChange" | "value"> & ControlProps) & { tooltipLabel?: string }) => {
        console.log('DateRange: render: props = ', props);
        return (
            <Control.Wrapper {...props}>
                {/* @ts-ignore */}
                <DateRangePicker
                    {...props}
                    toggleComponent={({ value }) => {
                        const ToggleComponent = () => (
                            <Input
                                value={`${value?.from ? moment(value?.from).format(`DD.MM.YYYY`) : `__.__.____`} - ${
                                    value?.to ? moment(value?.to).format(`DD.MM.YYYY`) : `__.__.____`
                                }`}
                                readOnly
                                extra={`background: transparent !important; min-width: 220px;`}
                                onChange={() => {}}
                                // disabled
                                placeholder={props?.placeholder}
                            />
                        );

                        if (props.disabled) {
                            console.log('props.disabled = ', props.disabled);
                            return (
                                <Tooltip label={props.tooltipLabel}>
                                    <ToggleComponent />
                                </Tooltip>
                            );
                        }

                        return <>
                            <ToggleComponent />
                        </>;
                    }}
                    withTime={false}
                    saveOnSelect={true}
                />
            </Control.Wrapper>
        );
    },
    Date: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <HoverDatePicker
                    {...props}
                    placeholder={props?.placeholder}
                    withTime={false}
                    saveOnSelect={true}
                />
            </Control.Wrapper>
        );
    },
    HTML: (props: any) => {
        return (
            <Control.Wrapper {...props} extra=".quill { width: 100%; }">
                <ReactQuill {...props} onChange={(e) => ({ target: { value: e } })} />
            </Control.Wrapper>
        );
    },
    Tags: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <TagsInput {...props} />
            </Control.Wrapper>
        );
    },
    TagsSelect: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <TagsSelect {...props} />
            </Control.Wrapper>
        );
    },
    Stars: (props: any) => {
        return (
            <Control.Wrapper {...props}>
                <StarsInput {...props} />
            </Control.Wrapper>
        );
    },
    Label: styled(Frame)<{ required?: boolean }>`
        color: ${({ theme }) => theme.text.primary};
        margin-bottom: 8px;
        width: auto !important;
        flex-direction: row;
        font-size: 14px;
        line-height: 20px;
        height: max-content;

        ${({ theme, required = false }) =>
            required &&
            css`
                &:after {
                    content: "*";
                    margin-left: 5px;
                    color: ${theme.red};
                }
            `}

        ${({ extra }) => extra}
    `,
    Error: styled(Frame)`
        color: ${(props) => props.theme.red};
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        min-height: 20px;
        margin-top: 5px;
        width: auto !important;
        height: max-content;

        ${({ extra }) => extra}
    `,
    Warning: styled(Frame)`
        color: ${(props) => props.theme.yellow};
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        height: 20px;
        margin-top: 5px;
        width: auto !important;

        ${({ extra }) => extra}
    `,
};
